import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const SECTIONS = [
  { id: 'quem-somos',     title: '1. Quem somos' },
  { id: 'dados',          title: '2. Dados que coletamos' },
  { id: 'base',           title: '3. Base legal para o tratamento' },
  { id: 'uso',            title: '4. Como usamos seus dados' },
  { id: 'ia',             title: '5. Inteligência artificial e decisões automatizadas' },
  { id: 'terceiros',      title: '6. Compartilhamento com terceiros' },
  { id: 'transferencias', title: '7. Transferências internacionais de dados' },
  { id: 'cookies',        title: '8. Cookies e rastreadores' },
  { id: 'direitos',       title: '9. Seus direitos' },
  { id: 'retencao',       title: '10. Retenção de dados' },
  { id: 'seguranca',      title: '11. Segurança e incidentes' },
  { id: 'menores',        title: '12. Menores de idade' },
  { id: 'ue',             title: '13. Usuários na União Europeia' },
  { id: 'alteracoes',     title: '14. Alterações nesta Política' },
  { id: 'contato',        title: '15. Contato e Encarregado de Dados' },
];

export function Privacy() {
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [dbHtml,      setDbHtml]      = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('legal_documents')
      .select('content_html, published_at')
      .eq('doc_type', 'privacy')
      .eq('is_current', true)
      .maybeSingle()
      .then(({ data }) => {
        if (data) { setPublishedAt(data.published_at); setDbHtml(data.content_html); }
      });
  }, []);

  const lastUpdated = publishedAt
    ? new Date(publishedAt).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '22 de abril de 2026';

  return (
    <>
      <Helmet>
        <title>Política de Privacidade, Professya</title>
        <meta name="description" content="Conheça como o Professya coleta, usa e protege seus dados pessoais, em conformidade com a LGPD e o GDPR." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://professya.com/privacidade" />
      </Helmet>
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>

          <Link to="/" style={{ color: 'var(--petrol)', textDecoration: 'none', fontSize: 'var(--text-label)', display: 'inline-block', marginBottom: '2rem' }}>
            ← Voltar ao início
          </Link>

          <h1 className="font-display" style={{ fontSize: 'clamp(1.5rem,4vw,2.25rem)', marginBottom: '0.5rem' }}>
            Política de Privacidade
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', marginBottom: '2rem' }}>
            Última atualização: {lastUpdated}
          </p>

          {dbHtml ? (
            <div dangerouslySetInnerHTML={{ __html: dbHtml }} />
          ) : (<>

          {/* ToC */}
          <div className="card" style={{ marginBottom: '2rem' }}>
            <p style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Índice</p>
            <ol style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 2 }}>
              {SECTIONS.map(s => (
                <li key={s.id}>
                  <a href={`#${s.id}`} style={{ color: 'var(--petrol)', fontSize: 'var(--text-label)' }}>{s.title}</a>
                </li>
              ))}
            </ol>
          </div>

          <section id="quem-somos" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>1. Quem somos</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              O Professya é uma plataforma digital de autoconhecimento baseada em astrologia, numerologia, tarô e práticas esotéricas. Operamos como controlador dos seus dados pessoais nos termos da Lei Geral de Proteção de Dados (LGPD, Lei nº 13.709/2018) e do Regulamento Geral de Proteção de Dados da União Europeia (GDPR, Regulamento UE 2016/679).
            </p>
          </section>

          <section id="dados" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>2. Dados que coletamos</h2>
            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>2.1 Dados de cadastro</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Nome, endereço de e-mail, data de nascimento (para verificação de maioridade) e senha (armazenada exclusivamente em formato criptografado, nunca em texto legível).
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>2.2 Dados sensíveis, convicções filosóficas e espirituais</h3>
            <div style={{ backgroundColor: 'rgba(31,78,95,0.06)', border: '1px solid rgba(31,78,95,0.2)', borderRadius: 'var(--radius-card)', padding: '0.875rem 1rem', marginBottom: '0.75rem' }}>
              <p style={{ color: 'var(--petrol)', fontSize: 'var(--text-label)', margin: 0 }}>
                <strong>Atenção:</strong> os dados descritos nesta seção são classificados como dados sensíveis nos termos do Art. 5º, II da LGPD e do Art. 9º do GDPR, por poderem revelar convicções filosóficas ou espirituais do usuário.
              </p>
            </div>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
              <li>Data, horário (opcional) e local de nascimento fornecidos para cálculo do mapa natal</li>
              <li>Histórico de consultas de tarô, I Ching e demais práticas esotéricas realizadas na plataforma</li>
              <li>Perfil de autoconhecimento derivado das análises personalizadas geradas para você</li>
              <li>Registros do Diário de Sonhos, quando utilizados</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              O tratamento desses dados exige e está baseado no seu <strong>consentimento explícito</strong>, específico e destacado, coletado separadamente dos dados de cadastro. Você pode revogar esse consentimento a qualquer momento, conforme descrito na seção 9.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>2.3 Preferências de comunicação</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Suas escolhas sobre receber ou não comunicações de marketing, alertas e notificações da plataforma.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>2.4 Dados de pagamento</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Processados diretamente por nosso processador certificado PCI-DSS. O Professya não armazena números de cartão de crédito, códigos de segurança ou chaves de pagamento em seus servidores.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>2.5 Dados de uso</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Funcionalidades acessadas, leituras geradas, consultas realizadas, previsões visualizadas, consentimentos registrados e interações com a plataforma.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>2.6 Dados técnicos</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Endereço IP, tipo de dispositivo, sistema operacional, versão do aplicativo e logs de erro.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>2.7 Cookies e identificadores</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              Cookies de sessão, preferências de idioma e registros de consentimento. Veja a seção 8 para detalhes completos.
            </p>
          </section>

          <section id="base" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>3. Base legal para o tratamento</h2>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.75rem' }}>3.1 Dados comuns (não sensíveis)</h3>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>Finalidade</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>Base legal LGPD</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>Base legal GDPR</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Prestação dos serviços contratados', 'Execução de contrato, Art. 7º, V', 'Art. 6º, 1(b)'],
                    ['E-mails transacionais e notificações de serviço', 'Execução de contrato, Art. 7º, V', 'Art. 6º, 1(b)'],
                    ['Marketing e comunicações promocionais', 'Consentimento, Art. 7º, I', 'Art. 6º, 1(a)'],
                    ['Alertas e notificações da plataforma', 'Consentimento, Art. 7º, I', 'Art. 6º, 1(a)'],
                    ['Cookies analíticos', 'Consentimento, Art. 7º, I', 'Art. 6º, 1(a)'],
                    ['Segurança, prevenção de fraude, diagnóstico técnico', 'Legítimo interesse, Art. 7º, IX', 'Art. 6º, 1(f)'],
                    ['Cumprimento de obrigações fiscais e legais', 'Obrigação legal, Art. 7º, II', 'Art. 6º, 1(c)'],
                    ['Dados de uso e logs de acesso', 'Legítimo interesse, Art. 7º, IX', 'Art. 6º, 1(f)'],
                  ].map(([finalidade, lgpd, gdpr]) => (
                    <tr key={finalidade} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-primary)' }}>{finalidade}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{lgpd}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{gdpr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>3.2 Dados sensíveis (convicções filosóficas e espirituais)</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              O tratamento dos dados descritos na seção 2.2 baseia-se exclusivamente no seu <strong>consentimento explícito</strong>, coletado de forma separada e específica.<br />
              Base legal: Art. 11º, I da LGPD e Art. 9º, 2(a) do GDPR.<br />
              Você pode revogar este consentimento a qualquer momento. A revogação não afeta a licitude do tratamento realizado antes dela, mas impedirá o uso futuro dos dados para novas análises personalizadas.
            </p>
          </section>

          <section id="uso" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>4. Como usamos seus dados</h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li>Verificar sua maioridade no cadastro e em atualizações de data de nascimento</li>
              <li>Calcular e exibir seu mapa natal, previsões, leituras e análises personalizadas</li>
              <li>Processar pagamentos e gerenciar sua assinatura</li>
              <li>Enviar notificações e e-mails transacionais (recibos, lembretes, alertas de serviço)</li>
              <li>Enviar comunicações de marketing, exclusivamente quando você tiver consentido</li>
              <li>Melhorar a qualidade e personalização dos serviços</li>
              <li>Monitorar a saúde técnica da plataforma e prevenir fraudes</li>
              <li>Cumprir obrigações legais, regulatórias e fiscais</li>
              <li>Registrar e honrar suas preferências de comunicação e privacidade</li>
            </ul>
          </section>

          <section id="terceiros" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>6. Compartilhamento com terceiros</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '0.75rem' }}>
              Compartilhamos seus dados apenas com fornecedores terceiros estritamente necessários para a prestação dos serviços. Todos operam como operadores de dados sob nossas instruções e estão contratualmente obrigados a proteger seus dados, incluindo a proibição de usar seus dados para treinar modelos de IA.
            </p>
            <div style={{ overflowX: 'auto', marginBottom: '0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>Categoria do fornecedor</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>Finalidade</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>País</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Provedor de infraestrutura em nuvem', 'Armazenamento de dados, autenticação e banco de dados', 'EUA'],
                    ['Provedor de inteligência artificial generativa', 'Geração das leituras e análises personalizadas', 'EUA'],
                    ['Processador de pagamentos certificado PCI-DSS', 'Processamento de cobranças, assinaturas e estornos', 'EUA'],
                    ['Provedor de comunicações digitais', 'Envio de e-mails transacionais e notificações', 'EUA'],
                    ['Provedor de hospedagem e entrega de conteúdo', 'Hospedagem da plataforma web e CDN global', 'EUA'],
                    ['Ferramenta de análise de uso (com consentimento)', 'Métricas agregadas de acesso e comportamento', 'EUA'],
                  ].map(([cat, purpose, country]) => (
                    <tr key={cat} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-primary)' }}>{cat}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{purpose}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{country}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              Não vendemos, alugamos nem comercializamos seus dados pessoais com terceiros para fins publicitários.
            </p>
          </section>

          <section id="ia" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>5. Inteligência artificial e decisões automatizadas</h2>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>5.1 Uso de inteligência artificial</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '0.75rem' }}>
              O Professya utiliza tecnologia de inteligência artificial generativa para criar leituras, previsões e análises personalizadas com base nos seus dados astrológicos. É importante que você compreenda:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem', marginBottom: '1rem' }}>
              <li><strong>Todo conteúdo entregue pela plataforma é gerado por inteligência artificial.</strong> Você está interagindo com conteúdo criado por IA, não por um astrólogo ou tarólogo humano.</li>
              <li><strong>As leituras têm caráter informativo, reflexivo e de entretenimento.</strong> Não constituem aconselhamento médico, psicológico, financeiro, jurídico, espiritual ou de qualquer outra natureza profissional. Decisões importantes devem ser tomadas com profissionais habilitados.</li>
              <li><strong>Os resultados podem variar.</strong> A IA pode ocasionalmente gerar conteúdos imprecisos ou inesperados. Implementamos filtros de qualidade, mas não garantimos 100% de precisão.</li>
            </ul>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>5.2 Seus dados e o treinamento de IA</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Seus dados pessoais, incluindo dados astrológicos, são utilizados exclusivamente para gerar as análises solicitadas por você. <strong>Não utilizamos seus dados para treinar modelos de inteligência artificial</strong>, seja internamente ou por meio de nossos provedores de tecnologia. Esta garantia está contratualmente estabelecida com todos os nossos fornecedores de IA.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>5.3 Direito de revisão humana</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              Nos termos do Art. 20 da LGPD e do Art. 22 do GDPR, você tem o direito de solicitar revisão humana de qualquer decisão tomada exclusivamente por meios automatizados que produza efeitos significativos sobre você. Para exercer este direito, entre em contato pelo e-mail indicado na seção 15.
            </p>
          </section>

          <section id="transferencias" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>7. Transferências internacionais de dados</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '0.75rem' }}>
              Todos os fornecedores listados na seção 6 estão sediados nos Estados Unidos. As transferências internacionais de dados são realizadas com base em:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li>Cláusulas Contratuais Padrão aprovadas pela Comissão Europeia (SCCs, Decisão de Execução 2021/914), para transferências de dados de usuários europeus</li>
              <li>Garantias contratuais equivalentes exigidas pela LGPD para transferências internacionais (Art. 33, II)</li>
            </ul>
          </section>

          <section id="cookies" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>8. Cookies e rastreadores</h2>
            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.75rem' }}>8.1 Tipos de cookies utilizados</h3>
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Nome funcional', 'Tipo', 'Finalidade', 'Duração', 'Pode ser recusado?'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Cookie de sessão',       'Essencial',    'Manter você autenticado durante o uso',              'Sessão',   'Não'],
                    ['Cookie de consentimento','Essencial',    'Registrar suas preferências de privacidade',         '12 meses', 'Não'],
                    ['Cookie de preferências', 'Preferências', 'Lembrar idioma e configurações de interface',        '12 meses', 'Sim'],
                    ['Cookie analítico',       'Analítico',    'Métricas agregadas de uso (apenas com consentimento)','13 meses', 'Sim'],
                  ].map(([nome, tipo, finalidade, duracao, recusavel]) => (
                    <tr key={nome} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-primary)' }}>{nome}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{tipo}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{finalidade}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{duracao}</td>
                      <td style={{ padding: '0.5rem', color: recusavel === 'Sim' ? 'var(--petrol)' : 'var(--text-muted)' }}>{recusavel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>8.2 Gerenciamento de cookies</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              Você pode revisar e alterar suas preferências de cookies a qualquer momento clicando em "Gerenciar Preferências de Cookies" no rodapé de qualquer página ou nas configurações do seu navegador. Cookies essenciais não podem ser desativados sem comprometer o funcionamento da plataforma. Rejeitar cookies não essenciais não prejudica o acesso às funcionalidades pagas que você contratou.
            </p>
          </section>

          <section id="direitos" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>9. Seus direitos</h2>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>9.1 Direitos garantidos pela LGPD (todos os usuários)</h3>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem', marginBottom: '1rem' }}>
              <li><strong>Confirmação e acesso:</strong> saber se tratamos seus dados e obter uma cópia completa</li>
              <li><strong>Correção:</strong> corrigir dados incompletos, inexatos ou desatualizados</li>
              <li><strong>Anonimização ou eliminação:</strong> solicitar a exclusão de dados desnecessários ou tratados em desconformidade</li>
              <li><strong>Portabilidade:</strong> receber seus dados em formato estruturado e legível por máquina</li>
              <li><strong>Informação sobre compartilhamento:</strong> saber com quais categorias de terceiros compartilhamos seus dados</li>
              <li><strong>Revogação de consentimento:</strong> retirar consentimento a qualquer momento, inclusive para dados sensíveis e marketing</li>
              <li><strong>Exclusão completa:</strong> solicitar a exclusão total da sua conta e de todos os dados associados (Art. 18, VI)</li>
              <li><strong>Revisão de decisão automatizada:</strong> solicitar revisão humana de análises geradas exclusivamente por IA (Art. 20)</li>
              <li><strong>Oposição:</strong> opor-se ao tratamento realizado com base em legítimo interesse</li>
            </ul>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>9.2 Exclusão completa de dados</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Ao solicitar a exclusão da sua conta, o Professya excluirá ou anonimizará todos os seus dados pessoais no prazo de 30 dias, com exceção dos dados que precisam ser mantidos por obrigação legal (registros fiscais por 5 anos; logs de acesso por 6 meses). Você receberá confirmação por e-mail quando o processo for concluído.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>9.3 Preferências de comunicação</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Você pode a qualquer momento, nas configurações do perfil, ativar ou desativar e-mails de marketing, notificações da plataforma e solicitar a remoção da lista de comunicações.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>9.4 Como exercer seus direitos</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              Por e-mail: <strong>suporte@professya.com</strong> com o assunto "Exercício de Direitos, [descreva seu pedido]"<br />
              Pelo aplicativo: Perfil → Privacidade → Exportar meus dados ou Excluir minha conta<br />
              <strong>Prazo de resposta:</strong> até 15 dias úteis (Brasil) / até 30 dias corridos (UE)
            </p>
          </section>

          <section id="retencao" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>10. Retenção de dados</h2>
            <div style={{ overflowX: 'auto', marginBottom: '0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Categoria de dados', 'Prazo de retenção', 'Justificativa'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Dados de cadastro (nome, e-mail)', 'Vigência da conta + 30 dias após exclusão', 'Execução de contrato'],
                    ['Data de nascimento de cadastro', 'Vigência da conta + 30 dias', 'Verificação de maioridade'],
                    ['Dados sensíveis (astrológicos, histórico esotérico)', 'Vigência da conta + 30 dias após exclusão', 'Consentimento'],
                    ['Diário de Sonhos', 'Vigência da conta; excluído imediatamente se solicitado', 'Consentimento'],
                    ['Histórico de leituras e previsões', 'Vigência da conta', 'Prestação do serviço'],
                    ['Preferências de comunicação e registros de consentimento', 'Vigência da conta + 5 anos', 'Comprovação de conformidade legal'],
                    ['Registros financeiros e fiscais', '5 anos após a transação', 'Obrigação legal (Lei 9.430/96)'],
                    ['Logs de acesso e dados técnicos', '6 meses', 'Marco Civil da Internet (Lei 12.965/2014)'],
                    ['Logs de auditoria de validação de idade', '5 anos', 'Comprovação de conformidade legal'],
                  ].map(([cat, prazo, just]) => (
                    <tr key={cat} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-primary)' }}>{cat}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{prazo}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{just}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              Após o prazo de retenção, os dados são excluídos de forma segura ou anonimizados de maneira irreversível.
            </p>
          </section>

          <section id="seguranca" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>11. Segurança e incidentes</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Adotamos medidas técnicas e organizacionais para proteger seus dados, incluindo criptografia em trânsito (TLS 1.3) e em repouso, controles de acesso por função, autenticação segura e monitoramento contínuo de anomalias.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>11.1 Monitoramento de fornecedores</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Monitoramos continuamente se nossos fornecedores e parceiros cumprem as obrigações contratuais de privacidade assumidas conosco, incluindo a proibição de usar seus dados para treinamento de IA e a obrigação de manter padrões adequados de segurança.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>11.2 Notificação de incidentes</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '0.5rem' }}>
              Em caso de incidente de segurança que resulte em risco para seus direitos e liberdades:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li>Notificaremos a Autoridade Nacional de Proteção de Dados (ANPD) dentro de 72 horas do conhecimento do incidente, conforme Art. 48 da LGPD</li>
              <li>Para usuários da União Europeia, notificaremos a autoridade supervisora europeia competente no mesmo prazo, conforme Art. 33 do GDPR</li>
              <li>Você será notificado por e-mail se seus dados forem afetados de forma que gere risco elevado, com informações sobre a natureza do incidente, dados envolvidos e medidas tomadas</li>
            </ul>
          </section>

          <section id="menores" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>12. Menores de idade</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '0.75rem' }}>
              O Professya é destinado exclusivamente a maiores de 18 anos. Verificamos a maioridade no cadastro e em qualquer atualização de data de nascimento. Não coletamos conscientemente dados de menores de idade.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '0.75rem' }}>
              Se você é pai, mãe ou responsável legal e suspeita que um menor criou uma conta, entre em contato imediatamente pelo e-mail <strong>suporte@professya.com</strong>. Excluiremos os dados assim que a situação for confirmada.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              Para usuários nos Estados Unidos: cumprimos o Children's Online Privacy Protection Act (COPPA). Usuários identificados como menores de 13 anos terão suas contas encerradas e dados excluídos sem aviso prévio.
            </p>
          </section>

          <section id="ue" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>13. Usuários na União Europeia (GDPR)</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Se você reside em um país membro da União Europeia ou no Espaço Econômico Europeu, o GDPR aplica-se integralmente ao tratamento dos seus dados.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>13.1 Direitos adicionais sob o GDPR</h3>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem', marginBottom: '1rem' }}>
              <li>Oposição ao tratamento baseado em legítimo interesse (Art. 21 GDPR), inclusive para fins de marketing direto</li>
              <li>Não ser sujeito a decisões exclusivamente automatizadas que produzam efeitos jurídicos ou igualmente significativos sobre você, com direito a solicitar intervenção humana e contestar a decisão (Art. 22 GDPR)</li>
              <li>Reclamação junto à autoridade supervisora do seu país de residência</li>
            </ul>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>13.2 Prazo de resposta</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Para usuários da UE, respondemos a pedidos de exercício de direitos em até 30 dias corridos, prorrogáveis por mais 60 dias em casos complexos, com comunicação prévia.
            </p>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>13.3 Autoridades supervisoras</h3>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem', marginBottom: '1rem' }}>
              <li><strong>Portugal:</strong> Comissão Nacional de Proteção de Dados (CNPD), cnpd.pt</li>
              <li><strong>Outros países da UE:</strong> a autoridade de proteção de dados do seu país de residência</li>
              <li><strong>Brasil:</strong> Autoridade Nacional de Proteção de Dados (ANPD), gov.br/anpd</li>
            </ul>

            <h3 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>13.4 Transferências de dados para os EUA</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              As transferências de dados de usuários europeus para nossos fornecedores nos Estados Unidos são realizadas com base em Cláusulas Contratuais Padrão aprovadas pela Comissão Europeia (SCCs, Decisão de Execução 2021/914), garantindo nível de proteção equivalente ao exigido pelo GDPR.
            </p>
          </section>

          <section id="alteracoes" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>14. Alterações nesta Política</h2>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li>Alterações significativas serão comunicadas por e-mail com 30 dias de antecedência</li>
              <li>Alterações menores (correções, esclarecimentos) serão indicadas pela atualização da data no topo desta página</li>
              <li>O uso continuado da plataforma após a data de vigência das alterações implica aceitação da nova versão</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginTop: '0.75rem' }}>
              Versões anteriores desta Política estão disponíveis mediante solicitação ao nosso Encarregado de Dados.
            </p>
          </section>

          <section id="contato" style={{ marginBottom: '3rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>15. Contato e Encarregado de Dados</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '0.75rem' }}>
              Para questões relacionadas à privacidade, proteção de dados ou exercício dos seus direitos:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem' }}>
              <li><strong>E-mail:</strong> suporte@professya.com</li>
              <li><strong>Assunto sugerido:</strong> "Privacidade, [descreva seu pedido]"</li>
              <li><strong>Prazo de resposta:</strong> até 15 dias úteis (Brasil) / 30 dias corridos (UE)</li>
              <li><strong>Autoridade no Brasil:</strong> Autoridade Nacional de Proteção de Dados (ANPD), gov.br/anpd</li>
              <li><strong>Autoridade em Portugal:</strong> Comissão Nacional de Proteção de Dados (CNPD), cnpd.pt</li>
            </ul>
          </section>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '1.5rem', color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.8 }}>
            <p>Professya, Segredo Astral | professya.com</p>
            <p style={{ marginTop: '0.5rem' }}>
              <Link to="/terms" style={{ color: 'var(--petrol)' }}>Termos de Uso</Link>
              {' · '}
              <Link to="/cancellation" style={{ color: 'var(--petrol)' }}>Cancelamento</Link>
              {' · '}
              <Link to="/" style={{ color: 'var(--petrol)' }}>Voltar ao início</Link>
            </p>
          </div>

          </>)}
        </div>
      </div>
    </>
  );
}
